// src/components/Authentication/SignInForm/ForgotPasswordModal.jsx

import { Dialog, DialogContent, Box, Typography, TextField, Button, Link, Alert } from "@mui/material";
import { useState, useContext } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../../firebase/firebase";
import { MailOutline, CheckCircleOutline, ArrowBack } from "@mui/icons-material";
import SpinningCustomLogo from '../../Common/SpinningCustomLogo';
import ModalContext from "../../../shared/ModalContext/ModalContext";

export const ForgotPasswordModal = ({ open, onClose }) => {
  const { closeModal } = useContext(ModalContext);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      setResetError("");
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
    } catch (error) {
      console.log("Something went wrong, try again", error);
      setResetError(error.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleClose = () => {
    setResetEmail("");
    setResetError("");
    setResetSuccess(false);
    setResetLoading(false);
    onClose();
  };

  const primaryColor = "#1976d2";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>

          {resetSuccess ? (
            <>
              <Box sx={{ 
                width: 64, height: 64, borderRadius: '50%', bgcolor: 'success.light', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 
              }}>
                <CheckCircleOutline sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                Check your email
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                We've sent a password reset link to <br/><strong>{resetEmail}</strong>.
              </Typography>
              <Button onClick={handleClose} variant="contained" fullWidth sx={{ 
                  bgcolor: primaryColor, '&:hover': { bgcolor: '#1565c0' }, py: 1.5,
                  borderRadius: '8px', textTransform: 'none', fontSize: '1rem'
                }}
              >
                Done
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ 
                width: 64, height: 64, borderRadius: '50%', bgcolor: 'primary.light', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                background: `linear-gradient(135deg, ${primaryColor} 0%, #29b6f6 100%)`
              }}>
                <MailOutline sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                Reset Password
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Enter your email and we’ll send you instructions to reset your password.
              </Typography>

              <Box component="form" onSubmit={handleForgotPassword} sx={{ width: '100%' }}>
                <TextField
                  label="Email Address" type="email" fullWidth required
                  value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email" sx={{ mb: 2 }}
                />
                
                {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}

                <Button type="submit" disabled={!resetEmail || resetLoading} variant="contained" fullWidth sx={{
                    bgcolor: primaryColor, '&:hover': { bgcolor: '#1565c0' }, py: 1.5,
                    borderRadius: '8px', textTransform: 'none', fontSize: '1rem',
                    boxShadow: `0 4px 14px 0 rgba(25, 118, 210, 0.39)`
                  }}
                >
                  {resetLoading ? <SpinningCustomLogo size={24} color="inherit" /> : "Continue"}
                </Button>

                <Link component="button" type="button" variant="body2" onClick={handleClose} sx={{ 
                    color: 'text.secondary', mt: 3, display: 'flex', alignItems: 'center', gap: 0.5 
                  }}
                >
                  <ArrowBack sx={{ fontSize: '1rem' }} /> Back to Sign In
                </Link>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};